import {
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  Flex,
  Input,
  VStack,
} from "@chakra-ui/react";
import DropFilesHere from "modules/messaging/_shared/drop-files-here";
import { usePanelSize } from "modules/providers/panel-size-provider";
import React, {
  ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDropzone } from "react-dropzone";
import { CHAT_DRAFT_ID } from "modules/utils/Constants";
import { useFile } from "modules/providers/file-provider";
import UserTagGroupSelector, {
  Option,
} from "modules/selectors/user-tag-group-selector";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import ComposeMessageThreadHeader from "../messaging/_shared/compose-message-thread-header";
import QuillMessageComposer, {
  IQuillEditorHandle,
} from "../messaging/compose/quill-message-composer";
import colors from "app/colors";
import FloatingLabel from "modules/_shared/floating-label";
import { MessageThreadType } from "gql/graphql";
import MessageThreadMessageList from "modules/messaging/message-thread/message-thread-message-list";
import useChats from "modules/hooks/use-chats";

const ComposeChatDrawer: React.FC = () => {
  const { uploadFiles } = useFile();
  const { rightWidth } = usePanelSize();
  const { draft, isDraftLoaded, updateCalloutUserIds, userValues, setSubject } =
    useMessageThreadDraft({
      draftId: CHAT_DRAFT_ID,
    });
  const { chat } = useChats({ accessUserIdList: draft?.calloutUserIdList });
  const quillEditorRef = useRef<IQuillEditorHandle>(null);

  /*https://teamcake.atlassian.net/browse/SYN-1485*/
  /*For some reason unless an internal state is used, the cursor jumps to the end*/
  const [_subject, _setSubject] = useState<string>();

  /*feels like another hack but needed when buffering the subject with a local variable*/
  useEffect(() => {
    //if the draft is empty, set the subject to empty, i.e. when the draft is cleared
    if (draft?.subject === "") {
      _setSubject("");
    }
  }, [_subject, draft?.subject]);

  const onFileListUpload = useCallback(
    (files: File[]) => uploadFiles(CHAT_DRAFT_ID, files),
    [uploadFiles],
  );

  const { getRootProps, isDragActive } = useDropzone({
    onDrop: onFileListUpload,
    noClick: true,
  });

  const onSelectedCalloutsChanged = useCallback(
    (options: Option[]) => {
      updateCalloutUserIds(options.map((option) => option.id));
    },
    [updateCalloutUserIds],
  );

  const onSubjectChanged = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      _setSubject(event.target.value);
      setSubject(event.target.value);
    },
    [setSubject],
  );

  if (!draft || !isDraftLoaded) return null;

  return (
    <DrawerContent {...getRootProps()} w={rightWidth} maxW={rightWidth}>
      <DropFilesHere isDragActive={isDragActive} width={rightWidth} />
      <DrawerHeader px={3} py={0}>
        <ComposeMessageThreadHeader
          draftId={CHAT_DRAFT_ID}
          title="New Chat"
          type={MessageThreadType.Chat}
          quillEditorRef={quillEditorRef}
        />
        <VStack pt={4} spacing={6}>
          <UserTagGroupSelector
            isMultiSelect
            types={["user"]}
            onSelectedOptionsChanged={onSelectedCalloutsChanged}
            placeHolder="Send to"
            floatingPlaceholder
            value={userValues}
            allowInactive={false}
          />
          <FloatingLabel label="Chat Name">
            <Input
              id="subject"
              placeholder="Optional"
              fontSize="sm"
              value={_subject || chat?.subject || draft.subject || ""}
              isDisabled={!!chat?.subject}
              maxLength={60}
              borderColor={colors.grayPrimary}
              _placeholder={{
                color: colors.placeholderText,
                fontWeight: 400,
                fontStyle: "italic",
              }}
              onChange={onSubjectChanged}
            />
          </FloatingLabel>
        </VStack>
      </DrawerHeader>

      <DrawerBody p={0} display="flex" flexDirection="column" overflow="hidden">
        {chat && (
          <Flex flexDir="column" flex={1} overflow="hidden" minH={0}>
            <MessageThreadMessageList messageThread={chat} />
          </Flex>
        )}
        <QuillMessageComposer
          draftId={CHAT_DRAFT_ID}
          type={MessageThreadType.Chat}
          expandOnInit
          ref={quillEditorRef}
        />
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(ComposeChatDrawer);
